import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"

import { cn } from "@/lib/utils"

interface CheckboxProps extends React.ComponentProps<typeof CheckboxPrimitive.Root> {
  /** Opt out of the tick-draw animation entirely. Mounting already checked
   *  (e.g. a remounted form or a reopened dropdown row) never animates, even
   *  when this is true — only a post-mount check does. Defaults to true. */
  animated?: boolean
}

function Checkbox({ className, ref, animated = true, onCheckedChange, ...props }: CheckboxProps) {
  const [tickArmed, setTickArmed] = React.useState(
    props.checked !== true && props.defaultChecked !== true
  )

  React.useEffect(() => {
    if (props.checked === false) setTickArmed(true)
  }, [props.checked])

  const handleCheckedChange = React.useCallback(
    (value: CheckboxPrimitive.CheckedState) => {
      setTickArmed(true)
      onCheckedChange?.(value)
    },
    [onCheckedChange]
  )

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "grid place-content-center peer h-6 w-6 shrink-0 rounded-xs border border-dinnerplate-white bg-off-white",
        "data-[state=checked]:bg-off-black data-[state=checked]:border-off-black",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onCheckedChange={handleCheckedChange}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="text-dinnerplate-white">
        <svg viewBox="0 0 18 18" fill="none" className="block h-6 w-6">
          {/* pathLength normalises the stroke so animate-checkbox-tick draws it on regardless of geometry. */}
          <path
            d="M3 9.5L7 13.5L15 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            className={animated && tickArmed ? "animate-checkbox-tick" : undefined}
          />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
